/**
 * Station colour-coding palette (issue #595, chunk S2).
 *
 * The roster heatmap colours each grid cell by its *station* so a long
 * grid is easier to scan. A station's colour comes from one of two
 * places:
 *
 *   1. An explicit `color` token set on the station's config
 *      (`StationEntry.color`, shipped by S1 / #594). The picker on the
 *      setup-page station editor writes this token.
 *   2. When `color` is unset (`null`), a **deterministic auto-assign**:
 *      a stable hash of the station name picks a palette token, so the
 *      same station gets the same colour on every render and in every
 *      cycle without anyone configuring anything.
 *
 * The backend does NOT enum-validate `color` — the frontend owns the
 * palette. An unrecognised stored token falls back to auto-assign so a
 * stale value never produces a blank cell.
 *
 * ── Why a hand-rolled palette and not design tokens ──────────────────
 * The semantic design tokens (`accent` / `success` / `warning` /
 * `danger`) only give four hues and several already carry meaning in
 * the heatmap (warning = on-call, danger = shortfall). Station colour
 * coding needs ~8–10 distinguishable, neutral hues. We therefore use a
 * fixed palette of muted Tailwind built-in colours — light, low-
 * saturation backgrounds (`*-100`) with a dark same-hue foreground
 * (`*-900`) so the cell text stays legible (WCAG-AA contrast). Each
 * token also carries a raw hex value so the proportional 24-hour
 * timeline bar (issue #606, `lib/timelineSegments.ts`) can paint a
 * shift segment with a concrete colour, not a utility class.
 */

/**
 * The ink to paint on top of any of this palette's light fills (#1713).
 *
 * The fills here are deliberately theme-INDEPENDENT (see the note above),
 * so the text over them cannot come from a theme foreground token — under
 * a dark theme `--color-fg` is near-white and would vanish on a pastel
 * band. This is therefore a palette token in its own right: the same
 * near-black slate the `slate` spec uses as its foreground, which clears
 * WCAG AA against every fill in the table below. Call sites reference this
 * instead of repeating the hex literal.
 */
export const FILL_INK = "#0f172a";

/** Ordered list of palette token ids. The setup-page picker renders a
 * swatch per token in this order; the auto-assign hash indexes into it. */
export const STATION_COLOR_TOKENS = [
  "slate",
  "sky",
  "emerald",
  "amber",
  "violet",
  "rose",
  "teal",
  "indigo",
  "lime",
  "fuchsia",
] as const;

export type StationColorToken = (typeof STATION_COLOR_TOKENS)[number];

interface StationColorSpec {
  /** Human-readable label for the picker's accessible name. */
  label: string;
  /** Tailwind background + foreground classes — used for the legend
   * swatch and a single-shift cell fill. Static strings so Tailwind's
   * content scanner keeps them. */
  cellClass: string;
  /** Tailwind classes for a small standalone swatch (picker / legend). */
  swatchClass: string;
  /** Raw light background colour — the fill a shift segment paints on
   * the proportional 24h timeline bar (#606). Tailwind `*-100`. */
  fill: string;
  /** Raw foreground colour — readable cell text on `fill`. Tailwind
   * `*-900`. */
  text: string;
}

/** Token → class + raw-colour spec. Colours are Tailwind's built-in
 * scale (theme-independent, accessible light/dark pairs). */
export const STATION_COLOR_SPECS: Record<StationColorToken, StationColorSpec> = {
  slate: {
    label: "Slate",
    cellClass: "bg-slate-100 text-slate-900",
    swatchClass: "bg-slate-200 border-slate-400",
    fill: "#f1f5f9",
    text: "#0f172a",
  },
  sky: {
    label: "Sky",
    cellClass: "bg-sky-100 text-sky-900",
    swatchClass: "bg-sky-200 border-sky-400",
    fill: "#e0f2fe",
    text: "#0c4a6e",
  },
  emerald: {
    label: "Emerald",
    cellClass: "bg-emerald-100 text-emerald-900",
    swatchClass: "bg-emerald-200 border-emerald-400",
    fill: "#d1fae5",
    text: "#064e3b",
  },
  amber: {
    label: "Amber",
    cellClass: "bg-amber-100 text-amber-900",
    swatchClass: "bg-amber-200 border-amber-400",
    fill: "#fef3c7",
    text: "#78350f",
  },
  violet: {
    label: "Violet",
    cellClass: "bg-violet-100 text-violet-900",
    swatchClass: "bg-violet-200 border-violet-400",
    fill: "#ede9fe",
    text: "#4c1d95",
  },
  rose: {
    label: "Rose",
    cellClass: "bg-rose-100 text-rose-900",
    swatchClass: "bg-rose-200 border-rose-400",
    fill: "#ffe4e6",
    text: "#881337",
  },
  teal: {
    label: "Teal",
    cellClass: "bg-teal-100 text-teal-900",
    swatchClass: "bg-teal-200 border-teal-400",
    fill: "#ccfbf1",
    text: "#134e4a",
  },
  indigo: {
    label: "Indigo",
    cellClass: "bg-indigo-100 text-indigo-900",
    swatchClass: "bg-indigo-200 border-indigo-400",
    fill: "#e0e7ff",
    text: "#312e81",
  },
  lime: {
    label: "Lime",
    cellClass: "bg-lime-100 text-lime-900",
    swatchClass: "bg-lime-200 border-lime-400",
    fill: "#ecfccb",
    text: "#365314",
  },
  fuchsia: {
    label: "Fuchsia",
    cellClass: "bg-fuchsia-100 text-fuchsia-900",
    swatchClass: "bg-fuchsia-200 border-fuchsia-400",
    fill: "#fae8ff",
    text: "#701a75",
  },
};

/**
 * Stable string hash (FNV-1a, 32-bit). Deterministic across renders,
 * cycles, and browser sessions — the same station name always maps to
 * the same palette token. Not cryptographic; it only needs an even-ish
 * spread over a ~10-slot palette.
 */
function hashStationName(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in uint32 range.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Auto-assign a palette token to a station purely from its name.
 * Deterministic — same name in → same token out.
 */
export function autoStationColorToken(stationName: string): StationColorToken {
  const idx = hashStationName(stationName) % STATION_COLOR_TOKENS.length;
  return STATION_COLOR_TOKENS[idx] as StationColorToken;
}

/** Type guard — is a stored config value a recognised palette token? */
export function isStationColorToken(value: string | null | undefined): value is StationColorToken {
  return value != null && (STATION_COLOR_TOKENS as readonly string[]).includes(value);
}

/**
 * Resolve a station to a concrete palette token.
 *
 * @param stationName     the station's name (drives the auto-assign hash)
 * @param configuredColor the `StationEntry.color` value — a token id,
 *                        `null` (auto-assign), or an unrecognised
 *                        stale string (also auto-assign).
 */
export function stationColorToken(
  stationName: string,
  configuredColor: string | null | undefined,
): StationColorToken {
  if (isStationColorToken(configuredColor)) return configuredColor;
  return autoStationColorToken(stationName);
}

/**
 * Resolve a station straight to its colour spec (class strings + raw
 * colours). Convenience over `stationColorToken` + a `STATION_COLOR_SPECS`
 * lookup at the call site.
 */
export function stationColor(
  stationName: string,
  configuredColor: string | null | undefined,
): StationColorSpec {
  return STATION_COLOR_SPECS[stationColorToken(stationName, configuredColor)];
}

// Issue #606 — `splitCellBackground` (the #595 equal-slice tilted-
// divider cell fill) was removed here. The roster cell body is now a
// proportional 24-hour timeline bar; its geometry lives in
// `lib/timelineSegments.ts` (`buildDayTimeline`) and it is rendered by
// `<RosterHeatmap/>`'s internal `DayTimelineBar`. The palette + auto-
// assign exports above are unchanged — the timeline still colours each
// shift segment by its station.
